import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { User } from "lucide-react";
import api from "@/lib/api";
import { resolveAssetUrl } from "@/lib/url";

const LOGO_URL = "/logo.png";
const DEFAULT_HERO_BG = "/hero-bg.jpg";

export const Hero = () => {
    const [s, setS] = useState({
        hero_title_line_1: "Przestrzeń mieszkańców",
        hero_title_line_2: "Dąbrówki Wiślanej",
        hero_subtitle: "Oficjalna strona wspólnoty mieszkaniowej Aluzyjna 25 C/D/E/F. Komunikaty, uchwały, wydarzenia i wszystko, co ważne dla naszej społeczności – w jednym miejscu.",
        hero_image_url: DEFAULT_HERO_BG,
        resident_panel_url: "",
    });

    useEffect(() => {
        api.get("/settings").then((r) => {
            const d = r.data || {};
            setS((prev) => ({
                hero_title_line_1: d.hero_title_line_1 || prev.hero_title_line_1,
                hero_title_line_2: d.hero_title_line_2 || prev.hero_title_line_2,
                hero_subtitle: d.hero_subtitle || prev.hero_subtitle,
                hero_image_url: d.hero_image_url || prev.hero_image_url,
                resident_panel_url: d.resident_panel_url || "",
            }));
        }).catch(() => {});
    }, []);

    const openResidentPanel = (e) => {
        const url = (s.resident_panel_url || "").trim();
        if (!url) {
            e.preventDefault();
            toast.info("Wkrótce pod tym przyciskiem będzie dostępny panel mieszkańca", {
                duration: 5000,
            });
        }
    };

    const lastWord = s.hero_title_line_2.split(" ").slice(-1)[0];
    const restWords = s.hero_title_line_2.split(" ").slice(0, -1).join(" ");

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100vh] flex items-center overflow-hidden"
        >
            <div className="absolute inset-0 z-0">
                <img src={resolveAssetUrl(s.hero_image_url)} alt="Osiedle Dąbrówka Wiślana" className="w-full h-full object-cover" />
                <div className="absolute inset-0 hero-gradient" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 w-full">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="lg:col-span-7 text-white"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs tracking-[0.2em] uppercase text-[#D4A537] mb-8">
                            <span className="w-2 h-2 rounded-full bg-[#D4A537] animate-pulse" />
                            Warszawa Białołęka • Aluzyjna 25
                        </div>

                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mb-6">
                            {s.hero_title_line_1} <br className="hidden md:block" />
                            {restWords && <>{restWords} </>}
                            <span className="text-[#D4A537] italic">{lastWord}</span>
                        </h1>

                        <p className="text-base md:text-lg text-white/85 max-w-xl mb-10 leading-relaxed">
                            {s.hero_subtitle}
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <a
                                href="#komunikaty"
                                data-testid="hero-cta-komunikaty"
                                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4A537] text-[#092E22] font-semibold hover:bg-[#E5B848] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-[#D4A537]/20"
                            >
                                Przejdź do komunikatów
                                <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                            </a>
                            <a
                                href={s.resident_panel_url || "#"}
                                target={s.resident_panel_url ? "_blank" : undefined}
                                rel={s.resident_panel_url ? "noopener noreferrer" : undefined}
                                onClick={openResidentPanel}
                                data-testid="hero-cta-resident-panel"
                                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#0D4F3C] font-semibold hover:bg-white/95 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-black/10 ring-2 ring-white/20"
                            >
                                <User size={16} className="transition-transform group-hover:scale-110" />
                                Panel mieszkańca
                            </a>
                            <a
                                href="#kontakt"
                                data-testid="hero-cta-kontakt"
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white hover:bg-white/10 transition-all duration-300"
                            >
                                Kontakt
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="lg:col-span-5 hidden lg:flex justify-center"
                    >
                        <div className="relative">
                            <div className="absolute -inset-8 rounded-full bg-[#D4A537]/20 blur-3xl" />
                            <picture>
                                <source srcSet="/logo.webp" type="image/webp" />
                                <img
                                    src={LOGO_URL}
                                    alt="Logo wspólnoty Dąbrówka Wiślana"
                                    width={384}
                                    height={256}
                                    className="relative w-80 h-80 xl:w-96 xl:h-96 object-contain drop-shadow-2xl"
                                    data-testid="hero-logo"
                                    loading="eager"
                                    decoding="async"
                                />
                            </picture>
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-[#FDFDFD]" />
        </section>
    );
};

export default Hero;
